import { LOG_SASS_RATE } from '@gramas/shared'
import { Button, NumberField, Sheet } from '@gramas/ui'
import { useEffect, useState } from 'react'
import { sassLine } from '../../sass/engine.js'
import { showSassToast } from '../../shell/toastStore.js'

export interface ActiveBurnSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  date: string
  current: number | null
  /**
   * What was copied off the watch the day before, to open on when today has
   * nothing yet. Active burn is a habit as much as a measurement — the same
   * commute, the same gym evenings — so yesterday's figure is a better starting
   * point than an empty box, and it is still typed over in one keystroke.
   */
  previous?: number | null
  onSave: (value: number | null) => Promise<void>
}

/**
 * Manual entry of the day's active calorie burn.
 *
 * No wearable API on offer exposes resting calories and the Huawei cloud path
 * is closed to individual developers, so this stays the dependable input even
 * once an automated bridge is running — the ring reads the same field either way.
 */
export function ActiveBurnSheet({
  open,
  onOpenChange,
  date,
  current,
  previous = null,
  onSave,
}: ActiveBurnSheetProps) {
  const [value, setValue] = useState<number | null>(current)
  const [saving, setSaving] = useState(false)
  const prefilled = current == null && previous != null

  useEffect(() => {
    if (open) setValue(current ?? previous)
  }, [open, current, previous])

  const commit = async () => {
    setSaving(true)
    try {
      await onSave(value)
      onOpenChange(false)
      // A burn entry earns a remark on the same odds as a log; clearing the
      // field to null is bookkeeping and stays silent.
      if (value != null && value > 0 && Math.random() < LOG_SASS_RATE) {
        const line = sassLine('burn_logged', {})
        if (line != null) showSassToast(line)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Aktyviai sudeginta"
      description={`${date} — perrašykite iš laikrodžio programėlės`}
      footer={
        <div className="flex gap-2">
          <Button block onClick={() => void onSave(null).then(() => onOpenChange(false))}>
            Išvalyti
          </Button>
          <Button variant="primary" block onClick={() => void commit()} disabled={saving}>
            Išsaugoti
          </Button>
        </div>
      }
    >
      <div className="px-4 py-4">
        <NumberField
          label="Aktyvios kalorijos"
          suffix="kcal"
          value={value}
          onChange={setValue}
          placeholder="0"
        />
        {prefilled && <p className="mt-2 text-sm text-mute">Įrašyta vakarykštė reikšmė.</p>}
        <p className="mt-3 text-sm text-mute">
          Ši reikšmė pridedama prie dienos limito, todėl deficitas skaičiuojamas pagal faktinį
          aktyvumą.
        </p>
      </div>
    </Sheet>
  )
}
